/**
 * liquid-glass.js
 * ═══════════════════════════════════════════════════════════════════════════
 * Apple-style "Liquid Glass" interaction module for the floating navigation.
 *
 * Provides two layers of visual enhancement:
 *
 *  1. CURSOR LIGHT TRACKING
 *     Smoothly interpolates --glass-light-x, --glass-light-y, and
 *     --glass-specular-opacity CSS custom properties on the nav element.
 *     These drive the ::after specular caustic and background highlight
 *     in CSS. Updates use requestAnimationFrame with lerp smoothing for
 *     60 fps fluid motion.
 *
 *  2. WEBGL REFRACTION OVERLAY (optional, graceful fallback)
 *     Creates a <canvas> inside the nav that renders animated caustic
 *     patterns via a GLSL fragment shader. The shader uses two octaves
 *     of gradient noise warped through sin() to produce bright caustic
 *     curves — similar to sunlight refracting through water or thick
 *     glass. The canvas runs at half resolution and blends via CSS
 *     mix-blend-mode: overlay.
 *
 * HTML-in-Canvas Integration:
 *  When the WICG HTML-in-Canvas API is available, the WebGL overlay
 *  is skipped — the LiquidGlassCanvas component handles refraction
 *  and dispersion by drawing the nav HTML directly into a WebGL
 *  texture via texElementImage2D. Cursor tracking still runs to feed
 *  light position to the canvas shader uniforms.
 *
 * Degradation:
 *  - HTML-in-Canvas → full shader refraction on real HTML content
 *  - No HTML-in-Canvas → CSS glass + WebGL caustic overlay
 *  - No WebGL → cursor tracking only (still dynamic)
 *  - No mouse → static glass, caustic animation still plays
 *
 * @module liquid-glass
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { isHtmlInCanvasSupported } from "./html-in-canvas"

// ─── Configuration ─────────────────────────────────────────────────────────

/** Smoothing factor for cursor following (higher = snappier, 0–1) */
const LIGHT_LERP_SPEED = 0.09

/** Speed at which the highlight returns to center when cursor leaves */
const IDLE_RETURN_SPEED = 0.035

/** WebGL canvas pixel ratio relative to CSS size (lower = faster) */
const CAUSTIC_RESOLUTION_SCALE = 0.5

// ─── Cursor Light Tracking ─────────────────────────────────────────────────

/**
 * Attaches mouse listeners to `el` and smoothly updates CSS custom
 * properties for the specular highlight position each frame.
 *
 * @param {HTMLElement} el - The nav element to track
 * @param {(x: number, y: number, hovering: boolean) => void} [onUpdate]
 * @returns {() => void} Cleanup function
 */
function initLightTracking(el, onUpdate) {
  let targetX = 50
  let targetY = 30
  let currentX = 50
  let currentY = 30
  let isHovering = false
  let rafId = null

  function tick() {
    const speed = isHovering ? LIGHT_LERP_SPEED : IDLE_RETURN_SPEED
    currentX += (targetX - currentX) * speed
    currentY += (targetY - currentY) * speed

    el.style.setProperty("--glass-light-x", `${currentX}%`)
    el.style.setProperty("--glass-light-y", `${currentY}%`)
    el.style.setProperty(
      "--glass-specular-opacity",
      isHovering ? "1" : "0"
    )

    if (onUpdate) onUpdate(currentX, currentY, isHovering)

    const settled =
      Math.abs(currentX - targetX) < 0.05 &&
      Math.abs(currentY - targetY) < 0.05

    if (!settled || isHovering) {
      rafId = requestAnimationFrame(tick)
    } else {
      rafId = null
    }
  }

  function ensureRunning() {
    if (rafId === null) rafId = requestAnimationFrame(tick)
  }

  function onMove(e) {
    const rect = el.getBoundingClientRect()
    targetX = ((e.clientX - rect.left) / rect.width) * 100
    targetY = ((e.clientY - rect.top) / rect.height) * 100
    isHovering = true
    ensureRunning()
  }

  function onLeave() {
    targetX = 50
    targetY = 30
    isHovering = false
    ensureRunning()
  }

  el.addEventListener("mousemove", onMove, { passive: true })
  el.addEventListener("mouseleave", onLeave, { passive: true })

  return function cleanup() {
    el.removeEventListener("mousemove", onMove)
    el.removeEventListener("mouseleave", onLeave)
    if (rafId !== null) cancelAnimationFrame(rafId)
  }
}

// ─── WebGL Refraction Overlay ──────────────────────────────────────────────

/** Minimal vertex shader — full-screen triangle strip. */
const VERT_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`

/**
 * Fragment shader — animated liquid caustics.
 *
 * Two octaves of gradient noise are warped through sin() to produce
 * bright caustic curves (like sunlight through water). A light-position
 * uniform concentrates the effect near the cursor. Subtle chromatic
 * offset splits R/B channels for a dispersive-glass look.
 */
const FRAG_SRC = `
precision mediump float;

uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_lightPos;

varying vec2 v_uv;

vec2 hash(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}

float gnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(dot(hash(i), f),
        dot(hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
    mix(dot(hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
        dot(hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x),
    u.y);
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 st = vec2(uv.x * aspect, uv.y);
  float t = u_time * 0.35;

  /* Two noise octaves warped through sin produce moving bright caustic
     curves — the same optical pattern you see at the bottom of a pool. */
  float n1 = gnoise(st * 5.0 + vec2(t, t * 0.7));
  float n2 = gnoise(st * 8.0 - vec2(t * 0.6, t * 0.4));
  float caustic = pow(abs(sin(n1 * 3.14159 + n2 * 2.0)), 4.0);

  /* Concentrate the pattern near the cursor/light position */
  vec2 ld = u_lightPos - uv;
  float falloff = smoothstep(1.0, 0.0, length(ld) * 0.9);

  /* Slight R/B channel split simulates chromatic dispersion */
  float chroma = sin(uv.x * 25.0 + t * 1.5) * 0.15;
  vec3 color = vec3(
    caustic * (1.0 + chroma),
    caustic,
    caustic * (1.0 - chroma)
  ) * falloff;

  gl_FragColor = vec4(color, caustic * falloff * 0.15);
}`

/**
 * Creates a WebGL canvas inside `el` that renders animated caustic
 * refraction patterns. Returns control object or null if WebGL
 * is unavailable.
 */
function initRefractionOverlay(el) {
  const canvas = document.createElement("canvas")
  canvas.className = "floating-nav__refraction"
  canvas.setAttribute("aria-hidden", "true")
  el.insertBefore(canvas, el.firstChild)

  const gl = canvas.getContext("webgl", {
    alpha: true,
    premultipliedAlpha: false,
    antialias: false,
    preserveDrawingBuffer: false,
  })

  if (!gl) {
    canvas.remove()
    return null
  }

  function compile(type, src) {
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s)
      return null
    }
    return s
  }

  const vs = compile(gl.VERTEX_SHADER, VERT_SRC)
  const fs = compile(gl.FRAGMENT_SHADER, FRAG_SRC)
  if (!vs || !fs) {
    canvas.remove()
    return null
  }

  const prog = gl.createProgram()
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    canvas.remove()
    return null
  }
  gl.useProgram(prog)

  // Full-screen quad
  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  )
  const aPos = gl.getAttribLocation(prog, "a_position")
  gl.enableVertexAttribArray(aPos)
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

  const uTime = gl.getUniformLocation(prog, "u_time")
  const uRes = gl.getUniformLocation(prog, "u_resolution")
  const uLight = gl.getUniformLocation(prog, "u_lightPos")

  let lightX = 0.5
  let lightY = 0.3
  let rafId = null
  let running = false
  const t0 = performance.now()

  function resize() {
    const r = el.getBoundingClientRect()
    const s = CAUSTIC_RESOLUTION_SCALE
    canvas.width = Math.round(r.width * s)
    canvas.height = Math.round(r.height * s)
    gl.viewport(0, 0, canvas.width, canvas.height)
  }

  function frame() {
    gl.uniform1f(uTime, (performance.now() - t0) / 1000)
    gl.uniform2f(uRes, canvas.width, canvas.height)
    gl.uniform2f(uLight, lightX, 1.0 - lightY)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    rafId = requestAnimationFrame(frame)
  }

  function start() {
    if (running) return
    running = true
    resize()
    canvas.classList.add("is-active")
    rafId = requestAnimationFrame(frame)
  }

  function stop() {
    if (!running) return
    running = false
    canvas.classList.remove("is-active")
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  // Only render when the nav is visible on screen
  const io = new IntersectionObserver(
    ([entry]) => {
      entry.isIntersecting ? start() : stop()
    },
    { threshold: 0.1 }
  )
  io.observe(el)

  // Re-size canvas when element dimensions change
  const ro = new ResizeObserver(() => {
    if (running) resize()
  })
  ro.observe(el)

  return {
    updateLight(x, y) {
      lightX = x / 100
      lightY = y / 100
    },
    cleanup() {
      stop()
      io.disconnect()
      ro.disconnect()
      gl.deleteProgram(prog)
      gl.deleteShader(vs)
      gl.deleteShader(fs)
      gl.deleteBuffer(buf)
      canvas.remove()
    },
  }
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Initialise all Liquid Glass effects on a navigation element.
 * Returns a cleanup function suitable for React useEffect teardown.
 *
 * When HTML-in-Canvas is available, skips the WebGL caustic overlay
 * (the LiquidGlassCanvas component handles refraction via
 * texElementImage2D instead). Cursor light tracking always runs.
 *
 * @param {HTMLElement | null} navElement
 * @param {object} [options]
 * @param {(x: number, y: number, hovering: boolean) => void} [options.onLightUpdate]
 *   Additional callback for light position changes (used to feed
 *   the HTML-in-Canvas shader uniforms).
 * @returns {() => void}
 */
export function initLiquidGlass(navElement, options = {}) {
  if (!navElement) return () => {}

  const htmlInCanvas = isHtmlInCanvasSupported()

  // When HTML-in-Canvas is active, skip the legacy WebGL overlay — the
  // LiquidGlassCanvas component renders shader refraction on the actual
  // nav HTML content via texElementImage2D instead.
  const refraction = htmlInCanvas ? null : initRefractionOverlay(navElement)

  const cleanupLight = initLightTracking(navElement, (x, y, hovering) => {
    if (refraction) refraction.updateLight(x, y)
    if (options.onLightUpdate) options.onLightUpdate(x, y, hovering)
  })

  return function cleanup() {
    cleanupLight()
    if (refraction) refraction.cleanup()
  }
}
